import { useState, useEffect, useMemo } from 'react';
import { 
  X, Shield, Check, Lock, AlertCircle, Headphones, 
  Monitor, Globe, Settings, Layers, UserCheck, Info,
  Mail, Building, Calendar, Key, User, Activity, CheckCircle2, Sliders
} from 'lucide-react';
import { AdminUserItem, useAdminStore } from '../../../store/adminStore';
import { useAuthStore } from '../../../store/authStore';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/Tabs';
import toast from 'react-hot-toast';

interface UserUnifiedDrawerProps {
  user: AdminUserItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Every role group shown in the assignment drawer. Grouping is driven by the
// authoritative `service` field (from the auth role hierarchy), not the free-text
// `category` column, so no assignable role is ever hidden. The final "other"
// bucket is a catch-all for any role whose service isn't one of the known keys.
const ROLE_GROUPS = [
  { key: 'global', name: 'Platform & Global', icon: Layers },
  { key: 'admin', name: 'Admin Platform', icon: Settings },
  { key: 'people_hub', name: 'People Hub', icon: UserCheck },
  { key: 'support', name: 'Support Service', icon: Headphones },
  { key: 'asset', name: 'Asset Management', icon: Monitor },
  { key: 'enduser', name: 'End User Portal', icon: Globe },
  { key: 'other', name: 'Other Roles', icon: Shield },
];

const KNOWN_SERVICE_KEYS = ['global', 'admin', 'people_hub', 'support', 'asset', 'enduser'];

export function UserUnifiedDrawer({
  user,
  isOpen,
  onClose,
  onSuccess,
}: UserUnifiedDrawerProps) {
  const { roles, loadRoles, assignUserRoles, auditLogs, inviteEmployee, toggleUserMfa, toggleServiceStatus } = useAdminStore();
  const currentAuthUser = useAuthStore((s) => s.user);
  const isSuperAdmin = Boolean(
    currentAuthUser?.role === 'super_admin' ||
    currentAuthUser?.role === 'admin' ||
    String(currentAuthUser?.role || '').toLowerCase().includes('admin') ||
    (currentAuthUser?.roles || []).some((r: any) => {
      const code = String(r?.code || r?.name || r || '').toLowerCase();
      return code.includes('super_admin') || code.includes('admin') || code.includes('platform');
    })
  );

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isTogglingService, setIsTogglingService] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'roles' | 'permissions' | 'activity'>('overview');

  useEffect(() => {
    if (isOpen) {
      loadRoles();
      setErrorMessage(null);
    }
  }, [isOpen, loadRoles]);

  useEffect(() => {
    if (user) {
      const initialIds = (user.roles || []).map((r) => r.id);
      setSelectedRoleIds(initialIds);
      setErrorMessage(null);
      setActiveTab('overview');
    }
  }, [user]);

  // Find effective permissions from selected roles (Unconditionally declared hooks)
  const assignedRolesList = useMemo(() => {
    return (roles || []).filter((r) => selectedRoleIds.includes(r.id));
  }, [roles, selectedRoleIds]);

  const effectivePermissions = useMemo(() => {
    const permsMap = new Map<string, { id: string; action: string; resource: string; module: string }>();
    assignedRolesList.forEach((r) => {
      (r.permissions || []).forEach((p) => {
        const key = `${p.module}:${p.resource}:${p.action}`;
        if (!permsMap.has(key)) {
          permsMap.set(key, p);
        }
      });
    });
    return Array.from(permsMap.values());
  }, [assignedRolesList]);

  if (!isOpen || !user) return null;

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || (user.email ? user.email.split('@')[0] : 'User');
  const targetUserId = user.user_id || user.id;

  // Show every role the admin can assign. Previously People/HR roles were filtered
  // out here; they are now included and grouped under "People Hub" below.
  const assignableRoles = roles.filter((r) => r.isPlatformRole ?? r.isInScope !== false);

  const toggleRole = (roleId: string) => {
    setErrorMessage(null);
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSaveRoles = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await assignUserRoles(targetUserId, selectedRoleIds);
      toast.success(res?.message || 'Roles updated successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to update roles';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvite = async () => {
    const targetId = user.employee_id || user.id;
    setIsInviting(true);
    try {
      await inviteEmployee(targetId);
      toast.success(`Account invitation sent to ${user.email}`);
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleToggleServiceUser = async () => {
    setIsTogglingService(true);
    try {
      const nextVal = !user.is_service_user;
      const res = await toggleServiceStatus(targetUserId, nextVal);
      toast.success(res.message);
      user.is_service_user = nextVal;
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update service status');
    } finally {
      setIsTogglingService(false);
    }
  };

  // Filter audit logs for this user
  const userAuditLogs = auditLogs.filter(
    (log) => log.user_id === user.id || log.user_id === user.user_id || log.user_email === user.email
  );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="p-4 border-b border-border flex items-center justify-between bg-card shrink-0">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold flex items-center justify-center text-sm shrink-0">
              {((user.first_name?.[0] || user.email?.[0] || 'U') as string).toUpperCase()}
              {user.last_name?.[0]?.toUpperCase() || ''}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-sm font-semibold text-foreground">{fullName}</h2>
                {user.is_service_user && (
                  <Badge variant="outline" className="text-[10px] bg-info/10 text-info border-info/20">
                    External/Service
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-mono">{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-lg hover:bg-secondary text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border bg-secondary/30 px-4 pt-2 shrink-0">
          <div className="flex space-x-1">
            {[
              { id: 'overview', label: 'Overview', icon: User },
              { id: 'roles', label: `Roles (${selectedRoleIds.length})`, icon: Shield },
              { id: 'permissions', label: `Permissions (${effectivePermissions.length})`, icon: Key },
              { id: 'activity', label: 'Activity', icon: Activity },
            ].map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer ${
                    isActive
                      ? 'border-primary text-primary font-semibold'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <TabIcon className="size-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <User className="size-3.5 text-primary" />
                  <span>Account &amp; Identity Details</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Employee Code</span>
                    <span className="font-semibold text-foreground font-mono">{user.employee_code || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Employee ID</span>
                    <span className="font-semibold text-foreground font-mono truncate block" title={user.employee_id || user.id}>
                      {user.employee_id ? user.employee_id.slice(0, 8) + '...' : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Department</span>
                    <span className="font-semibold text-foreground">{user.department || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Designation</span>
                    <span className="font-semibold text-foreground">{user.designation || 'Staff'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Account Status</span>
                    <div className="mt-0.5">
                      {user.has_account === false ? (
                        <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">
                          No Login Account
                        </Badge>
                      ) : user.status === 'active' ? (
                        <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
                          Active Account
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-secondary text-muted-foreground">
                          {user.status || 'Pending'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Two-Factor Authentication</span>
                    <span className="font-semibold text-foreground mt-0.5 flex items-center gap-1">
                      {user.mfa_enabled ? (
                        <span className="text-success font-semibold flex items-center gap-1">
                          <CheckCircle2 className="size-3.5" /> Enforced
                        </span>
                      ) : (
                        <span className="text-muted-foreground font-normal">Not Enforced</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block">
                  Identity Actions
                </span>
                <div className="flex flex-wrap gap-2">
                  {user.has_account === false ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleInvite}
                      disabled={isInviting}
                      className="text-xs"
                    >
                      {isInviting ? 'Sending Invite...' : 'Send Account Invitation'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setActiveTab('roles')}
                      className="text-xs"
                    >
                      <Shield className="size-3.5 mr-1 text-primary" />
                      <span>Manage Role Assignments</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* External / Service Classification (Super Admin Only) */}
              {isSuperAdmin && (
                <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-foreground text-xs flex items-center gap-1.5">
                        <Sliders className="size-3.5 text-primary" />
                        <span>External / Service Account Classification</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Designates this account for automated integrations, system workflows, or external contractors. Only a Super Admin can toggle this.
                      </p>
                    </div>
                    <Button
                      variant={user.is_service_user ? 'outline' : 'secondary'}
                      size="sm"
                      disabled={isTogglingService}
                      onClick={handleToggleServiceUser}
                      className="text-xs h-7 px-3 shrink-0"
                    >
                      {isTogglingService
                        ? 'Updating...'
                        : user.is_service_user
                        ? 'Unmark as Service'
                        : 'Mark as External/Service'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: ROLES BY SERVICE */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              <div className="p-3 bg-secondary/30 rounded-lg border border-border text-[11px] text-muted-foreground">
                Select roles below to grant scoped authority for this user across platform services.
              </div>

              {errorMessage && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-center gap-2 text-xs">
                  <AlertCircle className="size-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Roles Grouped by Service */}
              {ROLE_GROUPS.map((svc) => {
                const SvcIcon = svc.icon;
                const svcRoles = assignableRoles.filter((r) => {
                  const svcKey = (r.service || '').toLowerCase();
                  if (svc.key === 'other') return !KNOWN_SERVICE_KEYS.includes(svcKey);
                  return svcKey === svc.key;
                });

                if (svcRoles.length === 0) return null;

                return (
                  <div key={svc.key} className="border border-border rounded-lg bg-card overflow-hidden">
                    <div className="p-2.5 bg-secondary/40 border-b border-border flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <SvcIcon className="size-3.5 text-primary" />
                        <span className="font-semibold text-foreground text-xs">{svc.name}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {svcRoles.filter((r) => selectedRoleIds.includes(r.id)).length} / {svcRoles.length} assigned
                      </span>
                    </div>

                    <div className="p-2 divide-y divide-border/60">
                      {svcRoles.map((role) => {
                        const isSelected = selectedRoleIds.includes(role.id);
                        return (
                          <div
                            key={role.id}
                            onClick={() => toggleRole(role.id)}
                            className={`p-2.5 flex items-start justify-between rounded-md cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-secondary/40'
                            }`}
                          >
                            <div className="space-y-0.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-foreground">{role.name}</span>
                                {role.is_system && (
                                  <span title="System Role">
                                    <Lock className="size-3 text-muted-foreground" />
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground leading-relaxed">
                                {role.description || 'Standard role permissions'}
                              </p>
                            </div>
                            <div
                              className={`size-4 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                                isSelected
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'border-muted-foreground/40 bg-card'
                              }`}
                            >
                              {isSelected && <Check className="size-3 stroke-[3]" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 3: EFFECTIVE PERMISSIONS */}
          {activeTab === 'permissions' && (
            <div className="space-y-3">
              <div className="p-3 bg-secondary/30 rounded-lg border border-border text-[11px] text-muted-foreground">
                Showing all granular actions authorized across platform services based on active role assignments.
              </div>

              {effectivePermissions.length > 0 ? (
                <div className="border border-border rounded-lg bg-card overflow-hidden divide-y divide-border">
                  {effectivePermissions.map((perm, idx) => (
                    <div key={idx} className="p-2.5 flex items-center justify-between gap-2">
                      <div>
                        <span className="font-semibold text-foreground">{perm.resource}</span>
                        <span className="text-muted-foreground text-[11px] block">{perm.module}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px] font-mono uppercase bg-primary/10 text-primary border-primary/20">
                        {perm.action}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg">
                  No active permissions bound to this user. Assign roles in the Roles tab.
                </div>
              )}
            </div>
          )}

          {/* TAB 4: RECENT ACTIVITY */}
          {activeTab === 'activity' && (
            <div className="space-y-3">
              {userAuditLogs.length > 0 ? (
                <div className="border border-border rounded-lg bg-card divide-y divide-border">
                  {userAuditLogs.map((log) => (
                    <div key={log.id} className="p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground text-xs">{log.action}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {log.created_at ? new Date(log.created_at).toLocaleDateString() : 'Recent'}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Resource: {log.resource_type} {log.resource_id ? `(${log.resource_id.slice(0, 8)})` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg">
                  No audit activity recorded specifically for this user.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-border bg-card flex items-center justify-between shrink-0">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          {activeTab === 'roles' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveRoles}
              disabled={isSubmitting}
              className="gap-1.5"
            >
              <span>{isSubmitting ? 'Saving...' : 'Save Role Assignments'}</span>
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
